import { logger } from '@springtree/eva-sdk-core-logger';
// tslint:disable-next-line: import-name
import ky from 'ky-universal';

export interface IEvaServicePromise<RES> {
  serviceContext: string;
  promise: Promise<RES>;
  resolve: (value?: RES) => void;
  reject: (reason?: any) => void;
  created_at: Date;
  resolved_at?: Date;
}

// Each promise will be logged and stored here
// Once resolved they will be removed from the list
//
const promises: {[serviceContext: string]: IEvaServicePromise<any>} = {};

/**
 * Each promise chain has it's own promise that resolves when all the parts have
 * been collected and resolved
 *
 * @export
 * @interface IEvaServicePromiseChain
 */
export interface IEvaServicePromiseChain {
  parts: Promise<any>[];
  promise: Promise<any[]>;
}

// Promise chains are collected alongside the service chains
//
const chains: {[chainContext: string]: IEvaServicePromiseChain} = {};

/**
 * Registers a new EVA call promise
 *
 * @export
 * @template RES
 * @param {string} serviceContext The unique service context
 * @param {string} [chainContext] The unique chain this call belongs to
 * @returns {Promise<RES>}
 */
export function registerServicePromise<RES>(serviceContext: string, chainContext: string): Promise<RES|undefined> {

  // Create the call promise object and create wrapped
  // fetch and resolve methods that will cleanup the object
  // We need to build it partially first to satisfy required properties
  //
  const callPromise: Partial<IEvaServicePromise<RES|undefined>> = {
    serviceContext,
    created_at: new Date(),
  };

  // Add the promise and reject/resolve methods to the object
  //
  callPromise.promise = new Promise<RES|undefined>((resolve, reject) => {
    callPromise.resolve = (value?: RES) => {
      callPromise.resolved_at = new Date();
      resolve(value);
    };
    callPromise.reject = (reason: any) => {
      callPromise.resolved_at = new Date();
      reject(reason);
    };
  });

  // At this point the call promise object is complete
  //
  promises[serviceContext] = callPromise as IEvaServicePromise<RES|undefined>;

  // First call will setup the chain
  //
  if (!chains[chainContext]) {
    const parts: Promise<any>[] = [];
    chains[chainContext] = {
      parts,
      promise: (async () => {
        let responses: any[] = [];

        // Keep checking while the chain grows and/or is unresolved
        //
        do {
          try {
            responses = await Promise.all(parts);
          } catch (error) {
            logger.warn(`[EVA:SDK] Promise chain ${chainContext} failed`, error)();

            // If this an HTTPError from fetch we can try to extract the JSON response
            //
            if (error instanceof ky.HTTPError) {
              try {
                const httpError: ky.HTTPError = error;
                const jsonError = await httpError.response.json();

                // Re-throw to ensure this ends up as an error
                //
                if (jsonError) {
                  throw jsonError;
                }
              } catch (jsonErrorOrParseError) {
                logger.warn('[EVA:SDK:LOGIC] Failed to parse JSON error response', jsonErrorOrParseError)();
              }
            }
            throw error;
          }
        } while (responses.length !== parts.length);
        logger.debug(`[EVA:SDK] Promise chain ${chainContext} resolved with ${responses.length} calls`)();

        return responses;
      })(),
    };
  }

  // Add the call promise the chain
  //
  chains[chainContext].parts.push(callPromise.promise);

  return callPromise.promise;
}

/**
 * Retrieve a promise using its context
 *
 * @export
 * @param {string} context
 * @returns IEvaServicePromise<any>|void
 */
export function getServicePromise(serviceContext: string) {
  return promises[serviceContext];
}

/**
 * Retrieves a promise chain using its context
 *
 * @export
 * @param {string} chainContext
 * @returns
 */
export function getServicePromiseChain(chainContext: string) {
  return chains[chainContext];
}
